/**
 * Navigation Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace navigation
 */

import {register} from '@shopify/theme-sections';
import stickybits from 'stickybits';
import Flickity from 'flickity';

import 'flickity-imagesloaded';
import '../components/search';
import ProductCard from '../components/product-card';

export default register('header', {
  onLoad() {
    const self = this;
    window.searchIsVisible = false;
    this.isMobile = window.innerWidth < Number(window.theme.breakpoints.large);

    const btnMenuOpen = this.container.querySelector('.nav-toggle');
    if (btnMenuOpen) {
      btnMenuOpen.addEventListener('click', this.btnMenuClickToggleHandler.bind(this));
    }

    // Closes all dropdowns and nav drawer on browser resize and sets boolean for mobile width
    window.addEventListener('resize', this.handleResize.bind(this));

    const navOverlay = document.querySelector('.fader--nav');
    if (navOverlay) {
      navOverlay.addEventListener('click', this.btnMenuClickCloseHandler.bind(this));
    }

    const searchOverlay = document.querySelector('.fader--search');
    if (searchOverlay) {
      searchOverlay.addEventListener('click', this.hideModal.bind(this));
    }

    // Search toggle
    const btnSearch = this.container.querySelectorAll('.header__btn--search');
    if (btnSearch && btnSearch.length > 0) {
      btnSearch.forEach((btn) => btn.addEventListener('click', this.toggleModal.bind(this)));
    }

    // Search toggle from Page
    const btnSearchPage = document.querySelectorAll('.btn--search-toggle');
    if (btnSearchPage && btnSearchPage.length > 0) {
      btnSearchPage.forEach((btn) => btn.addEventListener('click', this.toggleModal.bind(this)));
    }

    // Level 1 toggles
    const navLevel1Toggles = this.container.querySelectorAll('.nav__item--toggle');
    if (navLevel1Toggles && navLevel1Toggles.length > 0) {
      navLevel1Toggles.forEach((el) => {
        el.addEventListener('click', self.hideModal);
        el.addEventListener('click', self.navLinkClickHandler);
      });
    }

    // Level 2 toggles
    const navLevel2Toggles = this.container.querySelectorAll('.mobile-subnav__item--toggle');
    if (navLevel2Toggles && navLevel2Toggles.length > 0) {
      navLevel2Toggles.forEach((el) => el.addEventListener('click', self.subNavToggleClickHandler));
    }

    // Level 2 all links
    const navLevel2Links = this.container.querySelectorAll('.nav__item--level-2-link');
    if (navLevel2Links && navLevel2Links.length > 0) {
      navLevel2Links.forEach((el) => el.addEventListener('mouseover', self.subNavLinkHoverHandler));
    }

    // Level 3 toggles
    const navLevel3SliderToggles = this.container.querySelectorAll('.subsubnav__item--toggle');
    if (navLevel3SliderToggles && navLevel3SliderToggles.length > 0) {
      navLevel3SliderToggles.forEach((el) => el.addEventListener('focus', self.subSubNavToggleFocusHandler));
    }

    // Level 3 all links
    const navLevel3Links = this.container.querySelectorAll('.nav__item--level-3-link');
    if (navLevel3Links && navLevel3Links.length > 0) {
      navLevel3Links.forEach((el) => el.addEventListener('mouseover', self.subSubNavLinkHoverHandler));
    }

    stickybits('#shopify-section-header', {
      useStickyClasses: true,
    });

    const productCards = this.container.querySelectorAll('.product-card');
    if (productCards && productCards.length > 0) {
      productCards.forEach((product) => ProductCard(product));
    }

    // Trigger to change the transparent header to white for overlay on home page. See top-of-site-pixel-anchor style for offset amount
    if (
      'IntersectionObserver' in window &&
      'IntersectionObserverEntry' in window &&
      'intersectionRatio' in window.IntersectionObserverEntry.prototype
    ) {
      // eslint-disable-next-line prefer-const
      let observer = new IntersectionObserver((entries) => {
        if (entries[0].boundingClientRect.y < 0) {
          document.body.classList.add('header-not-at-top');
        } else {
          document.body.classList.remove('header-not-at-top');
        }
      });
      observer.observe(document.querySelector('.top-of-site-pixel-anchor'));
    }
  },

  handleResize() {
    let windowWidth = window.innerWidth; // eslint-disable-line prefer-const

    if (this.isMobile === true && windowWidth >= Number(window.theme.breakpoints.large)) {
      this.btnMenuClickCloseHandler();
      this.isMobile = false;
    } else if (!this.isMobile && windowWidth < Number(window.theme.breakpoints.large)) {
      this.btnMenuClickCloseHandler();
      this.isMobile = true;
    }
  },

  /**
   * Click handler for search button or when an open top level item is open
   * Completely resets the open states of the navigation
   */
  collapseDropdown() {
    document.body.classList.remove('menu-is-expanded');
    document.body.classList.remove('dropdown-is-active');
    document.body.classList.remove('submenu-is-active');
    document.body.classList.remove('subsubmenu-is-active');

    // Level 1
    const level1ListItems = this.container.querySelectorAll('.nav__item--level-1');
    const level1Links = this.container.querySelectorAll('.nav__item--level-1 > .nav-link');
    if (level1ListItems && level1ListItems.length > 0) {
      level1ListItems.forEach((item) => {
        item.classList.remove('is-expanded');
      });
    }
    if (level1Links && level1Links.length > 0) {
      level1Links.forEach((link) => {
        link.removeAttribute('aria-expanded');
      });
    }

    // Level 2
    const level2ListItems = this.container.querySelectorAll('.nav__item--level-2');
    const level2Links = this.container.querySelectorAll('.nav__item--level-2-link');
    if (level2ListItems && level2ListItems.length > 0) {
      level2ListItems.forEach((item) => {
        item.classList.remove('subnav-is-expanded');
      });
    }
    if (level2Links && level2Links.length > 0) {
      level2Links.forEach((link) => {
        link.removeAttribute('aria-expanded');
      });
    }

    // Level 3
    const level3ListItems = this.container.querySelectorAll('.nav__item--level-3');
    const level3Links = this.container.querySelectorAll('.nav__item--level-3-link');
    if (level3ListItems && level3ListItems.length > 0) {
      level3ListItems.forEach((item) => {
        item.classList.remove('subsubnav-is-expanded');
      });
    }
    if (level3Links && level3Links.length > 0) {
      level3Links.forEach((link) => {
        link.removeAttribute('aria-expanded');
      });
    }
  },

  /**
   * .nav-toggle click handler
   */
  btnMenuClickOpenHandler() {
    document.body.classList.add('menu-is-expanded');
    // Hide the search modal if it's showing
    if (window.searchIsVisible) {
      this.hideModal();
    }

    const subMenuParents = document.querySelectorAll('.nav__item--has-child');
    if (subMenuParents && subMenuParents.length > 0) {
      subMenuParents.forEach(function (el) {
        if (!el.classList.contains('is-expanded')) {
          return;
        }
        el.classList.remove('is-expanded');
      });
    }
  },

  btnMenuClickCloseHandler() {
    this.collapseDropdown();
  },

  btnMenuClickToggleHandler() {
    if (document.body.classList.contains('menu-is-expanded')) {
      this.btnMenuClickCloseHandler();
    } else {
      this.btnMenuClickOpenHandler();
    }
  },

  /**
   * .nav__item.has-child > a click handler
   */
  navLinkClickHandler(event) {
    event.preventDefault();

    // Level 1 item toggle
    const navLevel1Toggles = document.querySelectorAll('.nav__item--toggle');
    const thisNavListItem = event.currentTarget.parentElement;
    const thisNavLink = event.currentTarget;

    // remove all current sub level attrs regardless of parent state
    const level2ToggleParents = document.querySelectorAll('.has-submenu');
    const level2Toggles = document.querySelectorAll('.mobile-subnav__item--toggle');
    const level3ToggleParents = document.querySelectorAll('.has-slider');
    const level3Toggles = document.querySelectorAll('.subsubnav__item--toggle');
    if (level2ToggleParents && level2ToggleParents.length > 0) {
      level2ToggleParents.forEach((item) => item.classList.remove('subnav-is-expanded'));
      level2Toggles.forEach((toggle) => toggle.removeAttribute('aria-expanded'));
    }
    if (level3ToggleParents && level3ToggleParents.length > 0) {
      level3ToggleParents.forEach((item) => item.classList.remove('subnav-is-expanded'));
      level3Toggles.forEach((toggle) => toggle.removeAttribute('aria-expanded'));
    }

    if (thisNavListItem && thisNavListItem.classList.contains('is-expanded')) {
      document.body.classList.remove('dropdown-is-active');
      document.body.classList.remove('submenu-is-active');
      document.body.classList.remove('subsubmenu-is-active');
      thisNavListItem.classList.remove('is-expanded');
      thisNavLink.removeAttribute('aria-expanded');
    } else {
      document.body.classList.add('dropdown-is-active');
      if (navLevel1Toggles && navLevel1Toggles.length > 0) {
        navLevel1Toggles.forEach((el) => {
          el.parentElement.classList.remove('is-expanded');
          el.removeAttribute('aria-expanded');
        });
      }

      if (thisNavListItem) {
        thisNavListItem.classList.add('is-expanded');
      }
      if (thisNavLink) {
        thisNavLink.setAttribute('aria-expanded', true);
      }
    }

    return true;
  },

  subNavToggleClickHandler(event) {
    event.preventDefault();

    const level2ToggleParents = document.querySelectorAll('.has-submenu');
    const level2Toggles = document.querySelectorAll('.mobile-subnav__item--toggle');
    const level3ToggleParents = document.querySelectorAll('.has-slider');
    const level3Toggles = document.querySelectorAll('.subsubnav__item--toggle');
    // Menu item toggle
    const thisLevel2Toggle = event.currentTarget;
    const thisLevel2Parent = event.currentTarget.parentElement;

    // Remove all subsublevel expansions regardless
    if (level3ToggleParents && level3ToggleParents.length > 0) {
      level3ToggleParents.forEach((item) => item.classList.remove('subnav-is-expanded'));
      level3Toggles.forEach((toggle) => toggle.removeAttribute('aria-expanded'));
    }

    // Check if current target is expanded
    if (thisLevel2Parent && thisLevel2Parent.classList.contains('subnav-is-expanded')) {
      document.body.classList.remove('submenu-is-active');
      if (thisLevel2Toggle) {
        thisLevel2Toggle.removeAttribute('aria-expanded');
        thisLevel2Parent.classList.remove('subnav-is-expanded');
      }
    } else {
      document.body.classList.add('submenu-is-active');
      if (level2ToggleParents && level2ToggleParents.length > 0) {
        level2ToggleParents.forEach((el) => el.classList.remove('subnav-is-expanded'));
        level2Toggles.forEach((el) => el.removeAttribute('aria-expanded'));
      }
      if (thisLevel2Toggle) {
        thisLevel2Parent.classList.add('subnav-is-expanded');
        thisLevel2Toggle.setAttribute('aria-expanded', true);
      }
    }

    return true;
  },

  subNavLinkHoverHandler(event) {
    // On large screens we don't want to trigger this toggle functionality
    if (window.innerWidth < Number(window.theme.breakpoints.large)) {
      return;
    }

    document.body.classList.add('submenu-is-active');
    const level2ToggleParents = document.querySelectorAll('.has-submenu');
    const level2Toggles = document.querySelectorAll('.mobile-subnav__item--toggle');
    const level3ToggleParents = document.querySelectorAll('.has-slider');
    const level3Toggles = document.querySelectorAll('.subsubnav__item--toggle');
    // Menu item toggle
    const thisLevel2Toggle = event.currentTarget;
    const thisLevel2Parent = event.currentTarget.parentElement;

    // Remove all subsublevel expansions regardless
    if (level3ToggleParents && level3ToggleParents.length > 0) {
      level3ToggleParents.forEach((item) => item.classList.remove('subnav-is-expanded'));
      level3Toggles.forEach((toggle) => toggle.removeAttribute('aria-expanded'));
    }

    // Check if current target is expanded
    if (thisLevel2Parent.classList.contains('subnav-is-expanded')) {
      return;
    } else {
      if (level2ToggleParents && level2ToggleParents.length > 0) {
        level2ToggleParents.forEach((el) => el.classList.remove('subnav-is-expanded'));
        level2Toggles.forEach((el) => el.removeAttribute('aria-expanded'));
      }
      if (thisLevel2Toggle && thisLevel2Toggle.classList.contains('mobile-subnav__item--toggle')) {
        thisLevel2Parent.classList.add('subnav-is-expanded');
        thisLevel2Toggle.setAttribute('aria-expanded', true);
      }
    }

    return true;
  },

  subSubNavToggleFocusHandler(event) {
    // On small screens we don't want to trigger this toggle functionality
    if (window.innerWidth < Number(window.theme.breakpoints.large)) {
      return;
    }

    event.preventDefault();

    const level3ToggleParents = document.querySelectorAll('.has-slider');
    const level3Toggles = document.querySelectorAll('.subsubnav__item--toggle');
    if (level3ToggleParents && level3ToggleParents.length > 0) {
      level3ToggleParents.forEach((item) => item.classList.remove('subnav-is-expanded'));
      level3Toggles.forEach((toggle) => toggle.removeAttribute('aria-expanded'));
    }

    const thisLevel3Toggle = event.currentTarget;
    const thisLevel3Parent = event.currentTarget.parentElement;
    const thisSlider = thisLevel3Parent.querySelector('.menuslider');
    const thisPreviousButton = thisLevel3Parent.querySelector('.btn--control-prev');
    const thisNextButton = thisLevel3Parent.querySelector('.btn--control-next');

    if (thisLevel3Parent && thisLevel3Parent.classList.contains('subsubnav-is-expanded')) {
      document.body.classList.remove('subsubmenu-is-active');
      thisLevel3Parent.classList.remove('subsubnav-is-expanded');
      thisLevel3Toggle.removeAttribute('aria-expanded');
    } else {
      document.body.classList.add('subsubmenu-is-active');
      if (thisLevel3Parent) {
        thisLevel3Parent.classList.add('subsubnav-is-expanded');
        thisLevel3Toggle.setAttribute('aria-expanded', true);
      }
    }

    if (thisSlider) {
      const flickity = new Flickity(thisSlider, {
        // options
        draggable: '>1',
        freeScroll: false,
        prevNextButtons: false,
        pageDots: false,
        imagesLoaded: true,
        setGallerySize: true,
        cellAlign: 'left',
        watchCSS: true,
        contain: true,
        on: {
          ready() {
            setTimeout(()=> {
              thisSlider.classList.add('is-visible');
            }, 400);
          },
          change() {
            if (!flickity.slides[flickity.selectedIndex - 1]) {
              thisPreviousButton.setAttribute('disabled', 'disabled');
              thisNextButton.removeAttribute('disabled');
              // eslint-disable-next-line no-negated-condition
            } else if (!flickity.slides[flickity.selectedIndex + 1]) {
              thisPreviousButton.removeAttribute('disabled');
              thisNextButton.setAttribute('disabled', 'disabled');
            } else {
              thisPreviousButton.removeAttribute('disabled');
              thisNextButton.removeAttribute('disabled');
            }
          },
        },
      });

      if (thisPreviousButton) {
        thisPreviousButton.addEventListener('click', function () {
          flickity.previous();
        });
        thisNextButton.addEventListener('click', function () {
          flickity.next();
        });
      }

      setTimeout(()=> {
        flickity.resize();
      }, 400);
    }

    return true;
  },

  subSubNavLinkHoverHandler(event) {
    // On small screens we don't want to trigger this toggle functionality
    if (window.innerWidth < Number(window.theme.breakpoints.large)) {
      return;
    }

    event.preventDefault();

    const level3ToggleParents = document.querySelectorAll('.has-slider');
    const level3Toggles = document.querySelectorAll('.subsubnav__item--toggle');
    const thisLevel3Link = event.currentTarget;
    const thisLevel3Parent = event.currentTarget.parentElement;

    if (thisLevel3Parent && thisLevel3Parent.classList.contains('subsubnav-is-expanded')) {
      return;
    } else {
      document.body.classList.add('subsubmenu-is-active');
      if (level3ToggleParents && level3ToggleParents.length > 0) {
        level3ToggleParents.forEach((item) => item.classList.remove('subsubnav-is-expanded'));
        level3Toggles.forEach((toggle) => toggle.removeAttribute('aria-expanded'));
      }

      if (thisLevel3Link && thisLevel3Link.classList.contains('subsubnav__item--toggle')) {
        thisLevel3Parent.classList.add('subsubnav-is-expanded');
        thisLevel3Link.setAttribute('aria-expanded', true);
      }
    }

    const thisSlider = thisLevel3Parent.querySelector('.menuslider');
    const thisPreviousButton = thisLevel3Parent.querySelector('.btn--control-prev');
    const thisNextButton = thisLevel3Parent.querySelector('.btn--control-next');

    if (thisSlider) {
      const flickity = new Flickity(thisSlider, {
        // options
        draggable: '>1',
        freeScroll: false,
        prevNextButtons: false,
        pageDots: false,
        imagesLoaded: true,
        setGallerySize: true,
        cellAlign: 'left',
        watchCSS: true,
        contain: true,
        on: {
          ready() {
            setTimeout(()=> {
              thisSlider.classList.add('is-visible');
            }, 400);
          },
          change() {
            if (!flickity.slides[flickity.selectedIndex - 1]) {
              thisPreviousButton.setAttribute('disabled', 'disabled');
              thisNextButton.removeAttribute('disabled');
              // eslint-disable-next-line no-negated-condition
            } else if (!flickity.slides[flickity.selectedIndex + 1]) {
              thisPreviousButton.removeAttribute('disabled');
              thisNextButton.setAttribute('disabled', 'disabled');
            } else {
              thisPreviousButton.removeAttribute('disabled');
              thisNextButton.removeAttribute('disabled');
            }
          },
        },
      });

      if (thisPreviousButton) {
        thisPreviousButton.addEventListener('click', function () {
          flickity.previous();
        });
        thisNextButton.addEventListener('click', function () {
          flickity.next();
        });
      }

      setTimeout(()=> {
        flickity.resize();
      }, 400);
    }

    return true;
  },

  /**
   * Toggles the search modal
   */
  toggleModal() {
    if (window.searchIsVisible) {
      this.hideModal();
    } else {
      this.showModal();
    }
  },

  /**
   * Expands the search modal
   */
  showModal() {
    const searchToggleButton = document.querySelector('.header__btn--search');
    const searchModal = document.querySelector('.search-modal');
    const searchInput = document.querySelector('.search-input');
    document.body.classList.add('search-is-visible');
    searchToggleButton.setAttribute('aria-expanded', true);
    searchModal.removeAttribute('hidden');
    setTimeout(() => {
      if (searchInput) {
        searchInput.focus();
      }
    }, 50);

    window.searchIsVisible = true;
    this.btnMenuClickCloseHandler();
  },

  /**
   * Collapses the search modal
   */
  hideModal() {
    const searchToggleButton = document.querySelector('.header__btn--search');
    const searchModal = document.querySelector('.search-modal');
    const searchInput = document.querySelector('.search-input');
    document.body.classList.remove('search-is-visible');
    searchToggleButton.setAttribute('aria-expanded', false);
    searchModal.setAttribute('hidden', 'hidden');

    if (searchInput) {
      searchInput.blur();
    }
    window.searchIsVisible = false;
  },
});
